var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { $ } from '../util/view';
import CMPError from '../cmp-error';
import API from './api';
import { requiresView } from './frontend-interface';
import { randomId } from '../util/string';
import { onContentLoaded } from '../util/dom';
import debug, { DEBUG, getLevelText, WARN } from '../util/debug';
import { searchParams } from '../util/location';
import { getCurrentPageChannel } from '../util/channel';
const isFrontendOnlyCommand = (_, propertyKey) => {
    FrontendAPI.frontendOnlyCommands.push(propertyKey);
};
export default class FrontendAPI extends API {
    constructor(backendBaseUrl, iframe, iframeWindow) {
        super(backendBaseUrl);
        this.iframe = iframe;
        this.iframeWindow = iframeWindow;
    }
    static load({ backendBaseUrl, consentString, cvlUiVersion, geoInfo, gvlUiVersion, nrvUiVersion, scriptVersionBaseUrl, vendorListBaseUrl, }) {
        const { document: doc } = window;
        const getIframeURL = () => {
            let url = `${scriptVersionBaseUrl}/html/iframe.html?mol.ads.cmp.log.level=${getLevelText()}&mol.geo.country=${geoInfo.geo}&mol.geo.region=${geoInfo.region}&clientConsentString=${consentString}`;
            if (searchParams['mol.ads.cmp.stubConsent']) {
                url += '&mol.ads.cmp.stubConsent';
            }
            else if (searchParams['mol.ads.cmp.stubDissent']) {
                url += '&mol.ads.cmp.stubDissent';
            }
            ;
            [
                ['backendBaseUrl', backendBaseUrl],
                ['cvlUiVersion', cvlUiVersion],
                ['gvlUiVersion', gvlUiVersion],
                ['nrvUiVersion', nrvUiVersion],
                ['vendorListBaseUrl', vendorListBaseUrl],
            ].forEach(([k, v]) => {
                url += `&${k}=${v}`;
            });
            const channel = getCurrentPageChannel();
            if (channel) {
                url += `&mol.ads.cmp.channel=${channel}`;
            }
            return url;
        };
        const iframe = $('iframe', {
            id: 'mol-ads-cmp-iframe',
            name: '__uspapiLocator',
            src: getIframeURL(),
            style: 'width:0;height:0;border:none;',
            title: 'MOL CMP iframe',
        }, [], doc).raw;
        return new Promise((resolve, reject) => {
            const onLoad = async () => {
                const { contentWindow } = iframe;
                iframe.removeEventListener('load', onLoad);
                if (!contentWindow) {
                    reject(new CMPError("Couldn't successfully create CMP"));
                }
                const api = new this(backendBaseUrl, iframe, contentWindow);
                resolve(api);
            };
            iframe.addEventListener('load', onLoad);
            iframe.addEventListener('error', reject);
            onContentLoaded(() => {
                const provisionalLocator = document.getElementById('mol-ads-cmp-iframe');
                if (provisionalLocator) {
                    provisionalLocator.remove();
                }
                doc.body.appendChild(iframe);
            });
        });
    }
    call(command, parameter = null, callback = () => { }) {
        debug(`calling __cmp(\'${command}\', '${parameter}')`, DEBUG);
        if (FrontendAPI.frontendOnlyCommands.includes(command)) {
            // @ts-ignore
            return this[command](parameter, callback);
        }
        const callId = randomId();
        this.createResponse(callId, (_, data) => {
            debug(`[${callId}] received`);
            debug(data);
            // TODO: Figure out why TS hates this line
            // @ts-ignore
            callback(data.returnValue, data.success);
        });
        debug(`[${callId}] calling ${command}(${parameter})`);
        this.iframeWindow.postMessage({
            __cmpCall: {
                callId,
                command,
                parameter,
            },
        }, '*');
    }
    consentToAll(origin = location.origin) {
        this.call('consentToAll', origin, () => { });
    }
    dissentToAll(origin = location.origin) {
        this.call('dissentToAll', origin, () => { });
    }
    setCCPANotApplicableStatus([origin = location.origin, channel = ''], callback) {
        this.call('setCCPANotApplicableStatus', [origin, channel], callback);
    }
    getUSPData(_, callback) {
        this.call('getUSPData', null, callback);
    }
    openSettings(_, callback) {
        this.view.openSettings();
        callback(null, true);
    }
    closeSettings(_, callback) {
        this.view.closeSettings();
    }
    giveThanks(_, callback) {
        this.view.alert();
        this.emit('thanksGiven');
        callback(null, true);
    }
    rendered(_, callback, count = 0) {
        if (this.view) {
            callback(null, true);
        }
        else if (count > 10) {
            callback(null, false);
        }
        else {
            setTimeout(() => this.rendered(_, callback, count + 1), 10);
        }
    }
    unload() {
        const { __uspapi: uspapi } = window;
        delete window.__uspapi;
        API.provision();
        for (const queueCommand of uspapi.a) {
            window.__uspapi.a.push(queueCommand);
        }
        this.iframe.remove();
    }
    createResponse(callId, fn) {
        const handler = ({ data, source }) => {
            if (source !== this.iframeWindow) {
                debug('message received from non CMP window', WARN);
                // return
            }
            let parsedData;
            try {
                parsedData = typeof data === 'string' ? JSON.parse(data) : data;
            }
            catch (error) {
                debug(error, WARN);
                return;
            }
            if (parsedData.__cmpReturn && parsedData.__cmpReturn.callId === callId) {
                window.removeEventListener('message', handler);
                fn(data, parsedData.__cmpReturn);
            }
        };
        window.addEventListener('message', handler);
    }
}
FrontendAPI.frontendOnlyCommands = [];
__decorate([
    isFrontendOnlyCommand,
    requiresView
], FrontendAPI.prototype, "openSettings", null);
__decorate([
    isFrontendOnlyCommand,
    requiresView
], FrontendAPI.prototype, "closeSettings", null);
__decorate([
    isFrontendOnlyCommand,
    requiresView
], FrontendAPI.prototype, "giveThanks", null);
__decorate([
    isFrontendOnlyCommand
], FrontendAPI.prototype, "rendered", null);
